/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { useEffect, useContext, useCallback } from 'react';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { useNavigate } from 'react-router';
import moment from 'moment';

import { initStoreAfterLogin } from 'common/stores/redux/store';
import RefreshOverlay from 'common/components/RefreshOverlay';
import Alert from 'common/components/Alert';
import { useAppDispatch } from 'common/stores/redux/hooks';
import { setAuthentication } from 'common/stores/redux/Authentication';
import { UserTokenContext } from 'useUserToken';
import { ServiceWorkerContext } from 'useServiceWorker';
import {
    _handleLoginResponse,
    _handleContractorLoginResponse,
    fetchSessionRedirect,
} from 'common/services/AuthService';
import * as logger from 'common/utils/Logger';
import { startTour } from 'common/guide-tour';
import { decodeJWT } from 'common/utils/token-utils';
import {
    AccessTokenKeys,
    AccessToken,
    MotiliToken,
} from 'common/types/access-token';
import { isLoggedIn } from 'common/services/LogoutService';

// TODO: Remove these types when useUserToken and useServiceWorker are converted to TypeScript
type UserTokenContextType = {
    setLoggedIn: (loggedIn: boolean) => Promise<void>;
};

type ServiceWorkerContextType = {
    updateServiceWorker: (autoUpdate: boolean) => Promise<void>;
};

const Auth0Redirect = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { getAccessTokenSilently, isAuthenticated: auth0Authenticated } =
        useAuth0();
    const userIsAuthenticated = auth0Authenticated && isLoggedIn();

    const { setLoggedIn } = useContext(
        UserTokenContext
    ) as UserTokenContextType;

    const { updateServiceWorker } = useContext(
        ServiceWorkerContext
    ) as ServiceWorkerContextType;

    const postLogin = useCallback(
        async (s2LoginResponse: MotiliToken) => {
            await _handleContractorLoginResponse();

            if (updateServiceWorker) {
                updateServiceWorker(true);
            }

            dispatch(setAuthentication(s2LoginResponse));

            initStoreAfterLogin();
            dispatch(startTour());
            await setLoggedIn(true);
        },
        [dispatch, updateServiceWorker, setLoggedIn]
    );

    useEffect(() => {
        async function handleS2LoginResponse() {
            try {
                const accessToken = await getAccessTokenSilently();
                const decodedToken = decodeJWT<AccessToken>(accessToken);
                if (!decodedToken?.[AccessTokenKeys.User]) {
                    throw new Error(
                        'Auth0Navigate: S2 login response is undefined'
                    );
                }
                const s2LoginResponse: MotiliToken = {
                    ...decodedToken?.[AccessTokenKeys.User],
                    lastCheckFromBackend: moment().valueOf(),
                };
                if (!s2LoginResponse.user) {
                    throw new Error(
                        'Auth0Navigate: user is null in S2 login response'
                    );
                }
                await _handleLoginResponse(s2LoginResponse);
                if (s2LoginResponse?.user?.forcePasswordChange) {
                    navigate('/updatePassword');
                } else {
                    await postLogin(s2LoginResponse);
                }
            } catch (error) {
                logger.error('Error during S2 login', error);
                Alert.error(
                    'There was an error processing the request for login, please contact support@motili.com if you need assistance logging into your account.',
                    { autoClose: 3000 }
                );
            }
        }

        if (userIsAuthenticated) {
            const redirectPath = fetchSessionRedirect();
            navigate(`/?redirectTo=${redirectPath}`, { replace: true });
        } else if (auth0Authenticated) {
            handleS2LoginResponse();
        }
    }, [
        getAccessTokenSilently,
        navigate,
        userIsAuthenticated,
        auth0Authenticated,
        postLogin,
    ]);

    return <RefreshOverlay />;
};

export default withAuthenticationRequired(Auth0Redirect, {
    onRedirecting: () => <RefreshOverlay />,
});
